import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HaloCatalogueItem } from '../services/api';
import './HaloItemPicker.css';

export const TERM_LABELS: Record<string, string> = { monthly: 'Monthly', annual: 'Annual', annual_prepaid: 'Annual, prepaid', flexi: 'Flexi' };

/** Search-as-you-type over the synced HaloPSA item catalogue (name, group or
 *  Xero code). Selecting calls onSelect with the item; the caller saves. */
export default function HaloItemPicker({ value, valueName, items, disabled, autoFocus, onSelect, onClear, onCancel }: {
  value: string | null; valueName: string | null; items: HaloCatalogueItem[]; disabled?: boolean; autoFocus?: boolean;
  onSelect: (item: HaloCatalogueItem) => void; onClear?: () => void; onCancel?: () => void;
}) {
  const [open, setOpen] = useState(!!autoFocus);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const words = needle.split(/\s+/).filter(Boolean);
    const hit = (i: HaloCatalogueItem) => {
      const hay = `${i.halo_item_id} ${i.name} ${i.group_name || ''} ${i.accounts_id || ''}`.toLowerCase();
      return words.every(w => hay.includes(w));
    };
    const list = words.length ? items.filter(hit) : items;
    return list.slice(0, 50);
  }, [q, items]);
  useEffect(() => { setIdx(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) { setOpen(false); onCancel?.(); }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onCancel]);
  const pick = (i: HaloCatalogueItem) => { onSelect(i); setOpen(false); setQ(''); };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (matches[idx]) pick(matches[idx]); }
    else if (e.key === 'Escape') { setOpen(false); setQ(''); onCancel?.(); }
  };
  return (
    <div className={`hi-picker ${value ? 'has-value' : ''}`} ref={boxRef}>
      <input
        className="hi-picker-input"
        placeholder={value ? `#${value}${valueName ? ` · ${valueName}` : ''}` : items.length ? 'Type to search Halo items…' : 'No Halo items synced yet'}
        value={q}
        autoFocus={autoFocus}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={onKey}
        title={value ? `Mapped to Halo item #${value}${valueName ? ` (${valueName})` : ''} - type to change` : 'Type a Halo item name, group or Xero code'}
      />
      {value && !q && onClear && (
        <button className="hi-picker-clear" disabled={disabled} onMouseDown={e => e.preventDefault()} onClick={onClear} title="Clear the Halo mapping">✕</button>
      )}
      {open && !disabled && (
        <ul className="hi-picker-list" role="listbox">
          {items.length === 0 && <li className="hi-picker-empty">Nothing synced yet - use "Sync Halo items" above.</li>}
          {items.length > 0 && matches.length === 0 && <li className="hi-picker-empty">No Halo item matches "{q}"</li>}
          {matches.map((i, n) => (
            <li key={i.halo_item_id} role="option" aria-selected={n === idx}
                className={`${n === idx ? 'active' : ''} ${String(i.halo_item_id) === value ? 'current' : ''} ${i.active ? '' : 'gone'}`}
                onMouseEnter={() => setIdx(n)} onMouseDown={e => { e.preventDefault(); pick(i); }}>
              <span className="hi-id">#{i.halo_item_id}</span>
              <span className="hi-name">{i.name}{String(i.halo_item_id) === value ? ' ✓' : ''}</span>
              {i.term && <span className={`hi-term hi-term-${i.term.replace(/[^a-z0-9]/g, '-')}`}>{TERM_LABELS[i.term] || i.term}</span>}
              <span className="hi-meta">
                {i.group_name || ''}{i.recurring_price || i.base_price ? ` · R ${i.recurring_price || i.base_price}` : ''}{i.active ? '' : ' · no longer in Halo'}
              </span>
            </li>
          ))}
          {items.length > matches.length && matches.length === 50 && <li className="hi-picker-empty">Keep typing to narrow down…</li>}
        </ul>
      )}
    </div>
  );
}
